/**
 * useSSESubscription Hook
 * React hook for managing SSE connections with:
 * - Heartbeat monitoring (45 second timeout)
 * - Exponential backoff reconnection (1s, 2s, 4s, 8s, 16s - max 5 attempts)
 * - Auto-cleanup on unmount
 * - Subscription stability (no duplicate connections on re-render)
 * 
 * Connection Error Handling (Requirements 10.1, 10.2, 10.3):
 * - Automatic reconnection with exponential backoff on connection errors
 * - Max retry limit of 5 attempts to prevent infinite reconnection loops
 * - Graceful fallback to push notifications after max retries
 * - All errors logged to Sentry for debugging
 * - No error messages shown to users - UI remains functional
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import { createSSEConnection, SSEStreamType, SSEConnectionState, SSEConnection } from '../services/sse';

// Heartbeat timeout in milliseconds (45 seconds)
const HEARTBEAT_TIMEOUT_MS = 45 * 1000;

// Retry configuration
const INITIAL_BACKOFF_MS = 1000;
const BACKOFF_MULTIPLIER = 2;
const MAX_RETRY_ATTEMPTS = 5;

interface UseSSESubscriptionResult {
    connectionState: SSEConnectionState;
    lastHeartbeat: Date | null;
    reconnectAttempt: number;
}

/**
 * Hook for subscribing to SSE streams with automatic reconnection
 * @param streamType - The type of stream to subscribe to
 * @param shopId - Shop ID for the subscription
 * @param onData - Callback when data is received
 * @param enabled - Whether the subscription is enabled (default: true)
 */
export function useSSESubscription<T>(
    streamType: SSEStreamType,
    shopId: string | null,
    onData: (data: T) => void,
    enabled: boolean = true
): UseSSESubscriptionResult {
    // State for UI feedback
    const [connectionState, setConnectionState] = useState<SSEConnectionState>('disconnected');
    const [lastHeartbeat, setLastHeartbeat] = useState<Date | null>(null);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);

    // Refs for connection management (survives re-renders)
    const connectionRef = useRef<SSEConnection | null>(null);
    const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMountedRef = useRef(true);

    // Stable ref for onData callback
    const onDataRef = useRef(onData);
    onDataRef.current = onData;

    // Calculate backoff delay based on attempt number (exponential: 1s, 2s, 4s, 8s, 16s)
    const getBackoffDelay = useCallback((attempt: number) => {
        return INITIAL_BACKOFF_MS * Math.pow(BACKOFF_MULTIPLIER, attempt);
    }, []);

    // Clear all timeouts
    const clearTimeouts = useCallback(() => {
        if (heartbeatTimeoutRef.current) {
            clearTimeout(heartbeatTimeoutRef.current);
            heartbeatTimeoutRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    // Close existing connection
    const closeConnection = useCallback(() => {
        if (connectionRef.current) {
            connectionRef.current.close();
            connectionRef.current = null;
        }
        clearTimeouts();
    }, [clearTimeouts]);

    // Reset heartbeat timeout
    const resetHeartbeatTimeout = useCallback(() => {
        if (heartbeatTimeoutRef.current) {
            clearTimeout(heartbeatTimeoutRef.current);
        }

        heartbeatTimeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;

            Sentry.addBreadcrumb({
                category: 'sse',
                message: 'Heartbeat timeout - scheduling reconnection',
                level: 'warning',
            });
            setConnectionState('error');
            closeConnection();

            // Check if we've exceeded max retry attempts
            if (reconnectAttempt >= MAX_RETRY_ATTEMPTS) {
                Sentry.captureMessage(`SSE: Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Stopping reconnection.`, 'warning');
                return;
            }

            // Calculate exponential backoff delay
            const backoffDelay = getBackoffDelay(reconnectAttempt);
            Sentry.addBreadcrumb({
                category: 'sse',
                message: `Reconnecting in ${backoffDelay}ms (attempt ${reconnectAttempt + 1}/${MAX_RETRY_ATTEMPTS})`,
                level: 'info',
            });

            reconnectTimeoutRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    setReconnectAttempt((prev) => prev + 1);
                }
            }, backoffDelay);
        }, HEARTBEAT_TIMEOUT_MS);
    }, [closeConnection, reconnectAttempt, getBackoffDelay]);

    // Connect to SSE
    const connect = useCallback(() => {
        if (!shopId || !enabled) {
            return;
        }

        Sentry.addBreadcrumb({
            category: 'sse',
            message: `Connecting to ${streamType} for shop ${shopId}`,
            level: 'info',
        });
        setConnectionState('connecting');

        connectionRef.current = createSSEConnection<T>(
            { streamType, shopId },
            {
                onData: (data) => {
                    if (isMountedRef.current) {
                        onDataRef.current(data);
                    }
                },
                onConnected: () => {
                    if (isMountedRef.current) {
                        Sentry.addBreadcrumb({
                            category: 'sse',
                            message: 'Connection established successfully',
                            level: 'info',
                        });
                        setConnectionState('connected');
                        setReconnectAttempt(0);
                        resetHeartbeatTimeout();
                    }
                },
                onHeartbeat: (data) => {
                    if (isMountedRef.current) {
                        Sentry.addBreadcrumb({
                            category: 'sse',
                            message: 'Heartbeat received',
                            level: 'debug',
                        });
                        setLastHeartbeat(new Date(data.timestamp));
                        resetHeartbeatTimeout();
                    }
                },
                onError: (error) => {
                    if (isMountedRef.current) {
                        Sentry.captureException(error, {
                            tags: { component: 'SSE Hook' },
                        });
                        setConnectionState('error');

                        // Don't auto-reconnect on explicit errors (like 404)
                        // Only reconnect on connection loss/heartbeat timeout
                        if (error instanceof Error) {
                            // Check if we've exceeded max retry attempts
                            if (reconnectAttempt >= MAX_RETRY_ATTEMPTS) {
                                Sentry.captureMessage(`SSE: Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached after error. Stopping reconnection.`, 'warning');
                                closeConnection();
                                return;
                            }

                            // Connection error - schedule reconnect with exponential backoff
                            closeConnection();
                            const backoffDelay = getBackoffDelay(reconnectAttempt);
                            Sentry.addBreadcrumb({
                                category: 'sse',
                                message: `Error - reconnecting in ${backoffDelay}ms (attempt ${reconnectAttempt + 1}/${MAX_RETRY_ATTEMPTS})`,
                                level: 'warning',
                            });

                            reconnectTimeoutRef.current = setTimeout(() => {
                                if (isMountedRef.current) {
                                    setReconnectAttempt((prev) => prev + 1);
                                }
                            }, backoffDelay);
                        }
                    }
                },
            }
        );

        // Start heartbeat monitoring
        resetHeartbeatTimeout();
    }, [shopId, enabled, streamType, resetHeartbeatTimeout, closeConnection, reconnectAttempt, getBackoffDelay]);

    // Main effect - manage connection lifecycle
    useEffect(() => {
        isMountedRef.current = true;

        if (shopId && enabled) {
            connect();
        }

        return () => {
            Sentry.addBreadcrumb({
                category: 'sse',
                message: 'Cleanup - closing connection',
                level: 'info',
            });
            isMountedRef.current = false;
            closeConnection();
            setConnectionState('disconnected');
        };
    }, [shopId, enabled, reconnectAttempt]); // reconnectAttempt triggers reconnection

    // Cleanup on streamType change (if ever needed)
    useEffect(() => {
        return () => {
            closeConnection();
        };
    }, [streamType, closeConnection]);

    return {
        connectionState,
        lastHeartbeat,
        reconnectAttempt,
    };
}
